import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Loader from '../../../components/Loader';
import Paginator from '../../../components/Paginator';
import ApiService from '../../../services/ApiService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import './List.css';

const MAROON = '#c4941a';

const List = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [partners, setpartners] = useState([]);
  const [pagination, setPagination] = useState({});
  const [apiData, setApiData] = useState({
    search: '',
    approval_status: '',
    status: '',
    sort_by: 'date-desc',
    page: 1,
    type,
  });

  useEffect(() => {
    setApiData((p) => ({ ...p, type }));
  }, [type]);

  useEffect(() => {
    getpartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData.page, apiData.search, apiData.approval_status, apiData.status, apiData.sort_by, apiData.type]);

  const getpartners = async () => {
    try {
      setIsLoader(true);
      const res = await ApiService.request({ method: 'GET', url: 'admin/partners', data: apiData });
      if (res.data.status) {
        setpartners(res.data.data || []);
        setPagination(res.data.pagination || {});
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error('Failed to fetch partners');
    } finally {
      setIsLoader(false);
    }
  };

  const delData = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonColor: '#9E9B9B',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await ApiService.request({ method: 'DELETE', url: `admin/partners/${id}` });
      if (res.data.status) { getpartners(); toast.success(res.data.message); }
      else toast.error(res.data.message);
    } catch { toast.error('Delete failed'); }
  };

  const updateStatus = async (id) => {
    const result = await Swal.fire({
      title: 'Change Status?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: MAROON,
      confirmButtonText: 'Yes',
      cancelButtonColor: '#9E9B9B',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await ApiService.request({ method: 'POST', url: `admin/partners/status/${id}` });
      if (res.data.status) { getpartners(); toast.success(res.data.message); }
      else toast.error(res.data.message);
    } catch { toast.error('Status update failed'); }
  };

  /* ── Computed stats from current list ── */
  const totalCount   = pagination.total_records ?? partners.length;
  const activeCount  = partners.filter((c) => c.status).length;
  const approvedCount = partners.filter((c) => c.approval_status === 'Approved').length;
  const pendingCount  = partners.filter((c) => c.approval_status === 'Pending').length;

  const getApprovalBadgeClass = (status) => {
    if (status === 'Approved') return 'approval-badge-approved';
    if (status === 'Rejected') return 'approval-badge-rejected';
    return 'approval-badge-pending';
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Breadcrumb toolbar */}
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              Service Providers
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">Service Providers</li>
            </ul>
          </div>
          <Link to="/admin/partners/create" className="btn btn-sm btn-base" >
            <i className="bi bi-plus-lg me-1"></i> Add Provider
          </Link>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">

          {/* Stats row */}
          <div className="row g-4 mb-6">
            {[
              { label: 'Total Providers', value: totalCount,    icon: 'bi-building',        bg: '#fdf8f9', iconColor: MAROON   },
              { label: 'Active',          value: activeCount,   icon: 'bi-check-circle',    bg: '#f0fdf4', iconColor: '#059669' },
              { label: 'Approved',        value: approvedCount, icon: 'bi-patch-check',     bg: '#eff6ff', iconColor: '#2563eb' },
              { label: 'Pending Approval',value: pendingCount,  icon: 'bi-clock-history',   bg: '#fffbeb', iconColor: '#d97706' },
            ].map(({ label, value, icon, bg, iconColor }) => (
              <div key={label} className="col-6 col-xl-3">
                <div className="partners-stat-card">
                  <div className="partners-stat-icon" style={{ background: bg }}>
                    <i className={`bi ${icon}`} style={{ color: iconColor }}></i>
                  </div>
                  <div className="partners-stat-info">
                    <div className="stat-value">{value?.toLocaleString() ?? 0}</div>
                    <div className="stat-label">{label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="row g-3 mb-6 align-items-center">
            <div className="col-md-3">
              <div className="partners-search-wrap">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  className="form-control form-control-solid"
                  placeholder="Search providers…"
                  value={apiData.search}
                  onChange={(e) => setApiData((p) => ({ ...p, search: e.target.value, page: 1 }))}
                />
              </div>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={apiData.approval_status}
                onChange={(e) => setApiData((p) => ({ ...p, approval_status: e.target.value, page: 1 }))}
              >
                <option value="">Approval Status</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={apiData.status}
                onChange={(e) => setApiData((p) => ({ ...p, status: e.target.value, page: 1 }))}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={apiData.sort_by}
                onChange={(e) => setApiData((p) => ({ ...p, sort_by: e.target.value, page: 1 }))}
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="name-asc">Name A–Z</option>
                <option value="name-desc">Name Z–A</option>
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-center gap-2">
              {(apiData.search || apiData.approval_status || apiData.status) && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setApiData((p) => ({ ...p, search: '', approval_status: '', status: '', page: 1 }))}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Grid */}
          {isLoader && partners.length === 0 ? (
            <div className="text-center py-10"><Loader text="Loading providers…" size={35} /></div>
          ) : partners.length === 0 ? (
            <div className="partners-empty">
              <i className="bi bi-building-slash fs-3x mb-3 d-block text-muted"></i>
              <div className="fs-6 text-muted">No service providers found</div>
            </div>
          ) : (
            <div className="row g-4">
              {partners.map((partner) => (
                <div key={partner._id} className="col-sm-6 col-xl-4 col-xxl-3">
                  <div className="partner-card">
                    {/* Cover — clickable */}
                    <div
                      className="partner-card-cover"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/partners/${partner._id}`)}
                    >
                      {partner.cover_images?.[0] ? (
                        <img src={partner.cover_images[0]} alt="cover" />
                      ) : (
                        <div className="partner-card-cover-placeholder">
                          <i className="bi bi-building text-white" style={{ fontSize: 36, opacity: 0.3 }}></i>
                        </div>
                      )}
                      <span className={`partner-card-status-badge ${partner.status ? 'approval-badge-approved' : 'approval-badge-inactive'}`}
                        style={{ cursor: 'pointer', border: 'none' }}
                        onClick={(e) => { e.stopPropagation(); updateStatus(partner._id); }}
                      >
                        {partner.status ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Logo — outside cover so it isn't clipped by overflow:hidden */}
                    <div className="partner-card-logo-circle">
                      {partner.logo ? (
                        <img src={partner.logo} alt={partner.company_name} />
                      ) : (
                        <div className="partner-card-logo-circle-placeholder">
                          <i className="bi bi-building-fill text-white"></i>
                        </div>
                      )}
                    </div>

                    {/* Body */}
                    <div className="partner-card-body">
                      <div
                        className="partner-card-name"
                        title={partner.company_name}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/partners/${partner._id}`)}
                      >
                        {partner.company_name}
                      </div>

                      {partner.categories?.length > 0 && (
                        <div className="partner-card-categories">
                          {partner.categories.slice(0, 2).map((cat, i) => (
                            <span key={i} className="partner-card-category-chip">{cat?.title || cat}</span>
                          ))}
                          {partner.categories.length > 2 && (
                            <span className="partner-card-category-chip">+{partner.categories.length - 2}</span>
                          )}
                        </div>
                      )}

                      <div className="partner-card-meta">
                        {partner.address && (
                          <div className="partner-card-meta-row">
                            <i className="bi bi-geo-alt-fill"></i>
                            <span>{partner.address}</span>
                          </div>
                        )}
                        {partner.phone && (
                          <div className="partner-card-meta-row">
                            <i className="bi bi-telephone-fill"></i>
                            <span>{partner.phone_code ? `+${partner.phone_code} ` : ''}{partner.phone}</span>
                          </div>
                        )}
                        {partner.email && (
                          <div className="partner-card-meta-row">
                            <i className="bi bi-envelope-fill"></i>
                            <span style={{ wordBreak: 'break-word' }}>{partner.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Approval badge row */}
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className={getApprovalBadgeClass(partner.approval_status)}>
                          {partner.approval_status || 'Pending'}
                        </span>
                      </div>

                      {/* Action buttons */}
                      <div className="partner-card-actions">
                        <Link
                          to={`/admin/partners/create/${partner._id}`}
                          className="partner-card-action-btn"
                          title="Edit"
                        >
                          <i className="bi bi-pencil-fill"></i>
                        </Link>
                        <button
                          type="button"
                          className="partner-card-action-btn danger"
                          title="Delete"
                          onClick={() => delData(partner._id)}
                        >
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="d-flex flex-stack flex-wrap pt-8">
              <div className="fs-6 fw-semibold text-gray-700">
                Showing {partners.length} of {pagination.total_records} providers
              </div>
              <Paginator
                page={pagination.current_page}
                rows={pagination.per_page}
                totalRecords={pagination.total_records}
                onPageChange={(p) => setApiData((prev) => ({ ...prev, page: p }))}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default List;
